import { DownloadOutlined } from '@ant-design/icons';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Button, Typography, QRCode } from 'antd';
import errorHandler from 'errors/errorHandler';
import { IEventTickets } from 'interfaces/attendanceList';
import { EventDates, IEvent } from 'interfaces/event';
import IUserToken from 'interfaces/tokens/userToken';
import React, { Fragment, memo, useEffect, useState } from 'react';
import ticketService from 'services/ticket';

import useStyles from './styles';
import userService from 'services/userService';
import { formatDate } from 'helpers/date';
import { format } from 'date-fns/format';

interface IProps {
  data: IEventTickets[];
  event: IEvent;
  eventImage?: string;
}

function PrintTemplate({ data, event, eventImage }: IProps) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<IUserToken>(null);

  useEffect(() => {
    userService.getUser().subscribe(user => {
      setLoading(false);
      setUser(user);
    });
  }, []);

  const downloadTicket = (eventId: string, inviteKey: string) => {
    setLoading(true);

    ticketService.print(eventId, inviteKey).subscribe(
      result => {
        const linkSource = `data:application/pdf;base64,${result.pdf}`;
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = `ticket_${inviteKey}.pdf`;
        downloadLink.click();
        setLoading(false);
      },
      err => {
        errorHandler(err);
        setLoading(false);
      },
    );
  };

  const attendancePrinted = (eventId: string, attendanceIds: string[]) => {
    ticketService.attendancePrinted(eventId, attendanceIds).subscribe();
  };

  const formatEventDate = (eventDate: EventDates) => {
    const date = formatDate(eventDate.date, 'dd/MM/yyyy');
    return `Dia ${date} das ${eventDate.start_time} às ${eventDate.end_time}`;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={50} />
      </div>
    );
  }
  return (
    <div style={{ backgroundColor: '#FFFFFF', display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
      {data?.map((item, index) => (
        <Fragment key={item.attendance_id}>
          <div className={classes.root} id={`ticket${index}`} style={{ marginTop: '16px' }}>
            {eventImage && (
              <div className={classes.logoArea}>
                <img
                  src={
                    eventImage ||
                    'https://app.blinket.com.br/static/media/new-blinket-logo.545d815330610cf13d224787684de5e3.svg'
                  }
                  alt='Logotipo'
                  height={200}
                  width={200}
                />
              </div>
            )}
            <div className={classes.topInfo}>
              <div>
                <div>
                  <Typography.Title level={5}>{event.title}</Typography.Title>
                </div>
                <div className={classes.ticketName}>
                  <Typography.Text strong>Ingresso: {item.ticket_name}</Typography.Text>

                  <Typography.Text strong>Lote: {item.lot_name}</Typography.Text>
                </div>

                <Typography>
                  <strong>Data: &nbsp;</strong>
                  {event.undefined_date === 1 ? (
                    'A definir'
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {event.event_dates.map(eventDate => (
                        <span key={eventDate.id}>{formatEventDate(eventDate)}</span>
                      ))}
                    </div>
                  )}
                </Typography>

                {event.place && event.type === 'presential' && (
                  <Typography>
                    <strong>Local: &nbsp;</strong>
                    {event.place}
                  </Typography>
                )}

                {event.type === 'online' && (
                  <Typography>
                    <strong>Evento Online</strong>
                  </Typography>
                )}
              </div>
            </div>

            <div>
              <div className={classes.ownerInfo}>
                {item.fat_cod && (
                  <div className={classes.invoiceNumber}>
                    <span className={classes.header}>Fatura:</span> <span className={classes.text}>{item.fat_cod}</span>
                  </div>
                )}

                <div>
                  <span className={classes.header}>Nome:</span> <span className={classes.text}>{item.name}</span>
                </div>

                <div>
                  <span className={classes.header}>Documento:</span>{' '}
                  <span className={classes.text}>{item.document}</span>
                </div>

                <div>
                  <span className={classes.header}>Nº Ingresso:</span>{' '}
                  <span className={classes.text}>{item.invite_key}</span>
                </div>

                {item.custom_fields &&
                  item.custom_fields.map(field => (
                    <div key={`custom-field-${field.label}`}>
                      <span className={classes.header}>{field.label}: </span>
                      <span className={classes.text}>{field.value}</span>
                    </div>
                  ))}

                {user && (
                  <div className={classes.requestedBy}>
                    Solicitado por: {user.user_name}, {format(new Date(), 'dd/MM/yyyy HH:mm:ss')}
                  </div>
                )}
              </div>
              <div>
                <QRCode value={item.invite_url || ''} size={240} bordered={false} />
              </div>
            </div>
            <div className='noprint' style={{ marginTop: '16px' }}>
              <Button
                onClick={() => {
                  downloadTicket(item.event_id, item.invite_key);
                  attendancePrinted(item.event_id, [item.attendance_id]);
                }}
                type='primary'
                icon={<DownloadOutlined />}
              >
                Baixar ingresso
              </Button>
            </div>
          </div>
          {index < data?.length - 1 && <div className='page-break' style={{ height: '16px', width: '100%' }} />}
        </Fragment>
      ))}
    </div>
  );
}

export default memo(PrintTemplate);
